'use client';

import { useSyncExternalStore } from 'react';

import { PORTFOLIO_SHELL_SNAPSHOT_KEY } from './portfolioShellBoot';

export { PORTFOLIO_SHELL_SNAPSHOT_KEY };

export type PortfolioShellSnapshot = {
  hasHoldings: boolean;
  quoteBarTickers: string[];
  version: 1;
};

const listeners = new Set<() => void>();

let cachedRaw: string | null | undefined;
let cachedSnapshot: PortfolioShellSnapshot | null = null;

const notify = () => {
  listeners.forEach(listener => listener());
};

const isTicker = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

export const getDefaultPortfolioShellSnapshot = (): PortfolioShellSnapshot => ({
  hasHoldings: false,
  quoteBarTickers: [],
  version: 1,
});

export const parsePortfolioShellSnapshot = (raw: string | null): PortfolioShellSnapshot | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const snapshot = parsed as Record<string, unknown>;
    if (
      snapshot.version !== 1 ||
      typeof snapshot.hasHoldings !== 'boolean' ||
      !Array.isArray(snapshot.quoteBarTickers)
    ) {
      return null;
    }
    return {
      hasHoldings: snapshot.hasHoldings,
      quoteBarTickers: snapshot.quoteBarTickers.filter(isTicker),
      version: 1,
    };
  } catch {
    return null;
  }
};

export const readPortfolioShellSnapshot = (): PortfolioShellSnapshot | null => {
  try {
    return parsePortfolioShellSnapshot(localStorage.getItem(PORTFOLIO_SHELL_SNAPSHOT_KEY));
  } catch {
    return null;
  }
};

const persistSnapshot = (snapshot: PortfolioShellSnapshot): void => {
  try {
    const raw = JSON.stringify(snapshot);
    localStorage.setItem(PORTFOLIO_SHELL_SNAPSHOT_KEY, raw);
    if (raw === cachedRaw) {
      return;
    }
    cachedRaw = raw;
    cachedSnapshot = snapshot;
    notify();
  } catch {
    return;
  }
};

export const writePortfolioShellSnapshot = (
  patch: Partial<Pick<PortfolioShellSnapshot, 'hasHoldings' | 'quoteBarTickers'>>,
): void => {
  const previous = readPortfolioShellSnapshot() ?? getDefaultPortfolioShellSnapshot();
  persistSnapshot({
    hasHoldings: patch.hasHoldings ?? previous.hasHoldings,
    quoteBarTickers: patch.quoteBarTickers ?? previous.quoteBarTickers,
    version: 1,
  });
};

const getClientSnapshot = (): PortfolioShellSnapshot | null => {
  try {
    const raw = localStorage.getItem(PORTFOLIO_SHELL_SNAPSHOT_KEY);
    if (raw === cachedRaw) {
      return cachedSnapshot;
    }
    cachedRaw = raw;
    cachedSnapshot = parsePortfolioShellSnapshot(raw);
    return cachedSnapshot;
  } catch {
    return null;
  }
};

const getServerSnapshot = (): PortfolioShellSnapshot | null => null;

const subscribe = (onStoreChange: () => void): (() => void) => {
  listeners.add(onStoreChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key !== PORTFOLIO_SHELL_SNAPSHOT_KEY && event.key !== null) {
      return;
    }
    cachedRaw = undefined;
    onStoreChange();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onStoreChange);
    window.removeEventListener('storage', onStorage);
  };
};

export const usePortfolioShellSnapshot = (): PortfolioShellSnapshot =>
  useSyncExternalStore(subscribe, getClientSnapshot, getServerSnapshot) ?? getDefaultPortfolioShellSnapshot();
